import type { ReactNode } from "react";

import {
  GridPreferencePanelsValue,
  gridDensityValueSelector,
  gridFilterActiveItemsSelector,
  gridPreferencePanelStateSelector,
  useGridApiContext,
  useGridSelector,
} from "@mui/x-data-grid-pro";
import {
  ColumnsIcon,
  DensityComfortableIcon,
  DensityCompactIcon,
  DensityStandardIcon,
  FilterIcon,
  V2Button,
  V2Divider,
  V2SingleSelectDropdown,
  V2SingleSelectDropdownItem,
  cn,
} from "@stacklet/ui";

import { XGridColumnsButton } from "./XGridColumnsButton";
import { XGridCustomToolbarExport } from "./XGridCustomToolbarExport";

import type { GridDensity } from "@mui/x-data-grid-pro";

const ICON_SIZE = 16;

const DENSITY_ICON: Record<GridDensity, React.ReactNode> = {
  compact: <DensityCompactIcon size={ICON_SIZE} />,
  standard: <DensityStandardIcon size={ICON_SIZE} />,
  comfortable: <DensityComfortableIcon size={ICON_SIZE} />,
};

interface Props {
  /** Handler invoked when the export button is pressed; omit to hide the button */
  handleExport?: () => void;
  /** Disables the export button while an export is running */
  exportInProgress?: boolean;
  /** Content rendered inline before the filter button (action buttons, search, etc.) */
  customInline?: ReactNode;
  /** Shows the column filter button when true */
  enableFilterButton?: boolean;
  /** Hides the density selector button when true */
  hideDensityButton?: boolean;
  /** Custom class name for the toolbar */
  className?: string;
}

export function XGridCustomToolbar({
  handleExport,
  exportInProgress = false,
  customInline,
  enableFilterButton = false,
  hideDensityButton = false,
  className,
}: Props) {
  const apiRef = useGridApiContext();
  const density = useGridSelector(apiRef, gridDensityValueSelector);
  const preferencePanel = useGridSelector(
    apiRef,
    gridPreferencePanelStateSelector,
  );
  const activeFilters = useGridSelector(apiRef, gridFilterActiveItemsSelector);

  function toggleFilterPanel() {
    const isFilterPanelOpen =
      preferencePanel.open &&
      preferencePanel.openedPanelValue === GridPreferencePanelsValue.filters;

    if (isFilterPanelOpen) {
      apiRef.current.hideFilterPanel();
    } else {
      apiRef.current.showFilterPanel();
    }
  }

  return (
    <div
      className={cn(
        "mb-sm flex w-full items-center justify-between gap-x-3xl rounded-sm px-md py-xs",
        className,
      )}
    >
      <div className="w-full"> {customInline}</div>
      <div className="flex h-full items-center gap-xs">
        {handleExport ? (
          <>
            <XGridCustomToolbarExport
              exportInProgress={exportInProgress}
              handleExport={handleExport}
            />
            <V2Divider orientation="vertical" variant="normal" />
          </>
        ) : null}
        {enableFilterButton ? (
          <V2Button
            IconLeft={<FilterIcon size={ICON_SIZE} />}
            aria-label="Filters"
            className="py-xxs"
            onPress={toggleFilterPanel}
            variant="tertiary"
          >
            {activeFilters.length > 0 ? activeFilters.length : null}
          </V2Button>
        ) : null}
        <XGridColumnsButton
          apiRef={apiRef}
          trigger={
            <V2Button
              IconLeft={<ColumnsIcon size={ICON_SIZE} />}
              aria-label="Columns"
              className="py-xxs"
              variant="tertiary"
            />
          }
        />
        {hideDensityButton ? null : (
          <V2SingleSelectDropdown
            AnchorEle={
              <V2Button
                IconLeft={DENSITY_ICON[density]}
                aria-label="Density"
                className="py-xxs"
                variant="tertiary"
              />
            }
            placement="bottom right"
          >
            <V2SingleSelectDropdownItem
              isSelected={density === "compact"}
              onPress={() => apiRef.current.setDensity("compact")}
            >
              <DensityCompactIcon size={ICON_SIZE} />
              Compact
            </V2SingleSelectDropdownItem>
            <V2SingleSelectDropdownItem
              isSelected={density === "standard"}
              onPress={() => apiRef.current.setDensity("standard")}
            >
              <DensityStandardIcon size={ICON_SIZE} />
              Standard
            </V2SingleSelectDropdownItem>
            <V2SingleSelectDropdownItem
              isSelected={density === "comfortable"}
              onPress={() => apiRef.current.setDensity("comfortable")}
            >
              <DensityComfortableIcon size={ICON_SIZE} />
              Comfortable
            </V2SingleSelectDropdownItem>
          </V2SingleSelectDropdown>
        )}
      </div>
    </div>
  );
}
